import emptyTranscriptionsSrc from 'Assets/images/empy-states/empty-transcriptions.png?inline';
import InfiniteList from 'Components/shared/InfiniteList';
import { HistoryChatBubble } from 'Modules/videoHistory/components/HistoryChatBubble';
import { NetworkEmptyState } from 'Modules/videoHistory/components/NetworkEmptyState';
import { useFetchTranscriptions } from 'Modules/videoHistory/requests';
import {
  formatElapsedMmSs,
  isNetworkFetchResult,
  isTranscription,
  mergeSegments,
} from 'Modules/videoHistory/utils';
import { isBrowserOffline } from 'Modules/videoHistory/utils/isNetworkTransportError';
import React from 'react';
import { EmptyState } from '~/lib/ui/emptyState';
import {
  TRANSCRIPTIONS_LIST_EMPTY_STATE_DELETED,
  TRANSCRIPTIONS_LIST_EMPTY_STATE_UNAVAILABLE,
} from './index.constants';
import { TranscriptionsListProps } from './index.types';

export const TranscriptionsList = ({
  conferenceId,
  isDeleting,
  testId,
  fetchEnabled = true,
}: TranscriptionsListProps) => {
  const { data, fetchNextPage, isFetching, isFetchingNextPage, hasNextPage } =
    useFetchTranscriptions(conferenceId, { enabled: fetchEnabled });

  const firstPage = data?.pages[0];
  const transcription = isTranscription(firstPage) ? firstPage : undefined;

  const transcriptionSegments =
    data?.pages?.flatMap((page) =>
      isTranscription(page) ? page.segments : []
    ) ?? [];

  const mergedTranscriptionSegments = mergeSegments(transcriptionSegments);
  const hasTranscriptionSegments =
    (mergedTranscriptionSegments?.length ?? 0) > 0;

  const showNetworkEmpty =
    isNetworkFetchResult(firstPage) ||
    (isBrowserOffline() &&
      (firstPage === undefined ||
        firstPage === null ||
        !hasTranscriptionSegments));

  const shouldShowEmptyState =
    (isDeleting || !mergedTranscriptionSegments?.length) && !isFetching;

  const shouldShowItems =
    !shouldShowEmptyState && hasTranscriptionSegments;

  const isListLoading =
    (isFetching && !data) ||
    (isFetching && !isFetchingNextPage && !hasTranscriptionSegments) ||
    isFetchingNextPage;

  const componentTestId = testId ?? 'transcriptions-list';

  const handleLoadMore = React.useCallback(() => {
    if (hasNextPage && !isFetching) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetching, fetchNextPage]);

  if (showNetworkEmpty) {
    return <NetworkEmptyState testID={`${componentTestId}-empty-state`} />;
  }

  const emptyStateDescription =
    transcription?.status === 'DELETION_PENDING'
      ? TRANSCRIPTIONS_LIST_EMPTY_STATE_DELETED
      : TRANSCRIPTIONS_LIST_EMPTY_STATE_UNAVAILABLE;

  return (
    <InfiniteList
      onScroll={handleLoadMore}
      isLoading={isListLoading}
      className="pl-4.5 pr-6"
      emptyState={
        shouldShowEmptyState ? (
          <EmptyState
            description={emptyStateDescription}
            illustration={
              <img
                src={emptyTranscriptionsSrc}
                alt=""
                aria-hidden
                className="block max-w-full object-contain"
              />
            }
            className="h-[65%] min-h-fit"
            descriptionClassName="whitespace-pre-line"
            testID={`${componentTestId}-empty-state`}
          />
        ) : null
      }
      testId={componentTestId}
    >
      {shouldShowItems && (
        <>
          <div key="transcription-start" className="mb-4.5">
            <div className="mb-1">
              <span className="text-body font-bold mr-4.5">
                Transcription has started
              </span>
              <span className="text-subtitles text-grey-700">00:00</span>
            </div>
            <span className="text-subtitles">
              Transcription generated by AI
            </span>
          </div>
          {mergedTranscriptionSegments?.map((segment) => (
            <HistoryChatBubble
              key={segment.id}
              text={segment.text}
              personId={segment.personId}
              displayName={segment.displayName}
              timestamp={
                transcription
                  ? formatElapsedMmSs(
                      transcription.startedAt.valueOf(),
                      segment.timestamp.valueOf()
                    )
                  : ''
              }
              testId={`${componentTestId}-history-chat-bubble`}
            />
          ))}
        </>
      )}
    </InfiniteList>
  );
};
